import { useTranslation } from 'react-i18next'
import { useReducedMotion } from '@/shared/hooks/useReducedMotion'
import { haptic } from '@/shared/utils/haptics'
import { useOfflineTickStore } from './useOfflineTickStore'
import type { GroceryListItem } from './types'

interface GroceryItemRowProps {
  item: GroceryListItem
  onTick: (item: GroceryListItem) => void
}

export function GroceryItemRow({ item, onTick }: GroceryItemRowProps) {
  const { t } = useTranslation('grocery-list')
  const reducedMotion = useReducedMotion()
  const pendingItemIds = useOfflineTickStore((s) => s.pendingItemIds)
  const isPendingSync = pendingItemIds.has(item.id)

  const ariaLabel = item.ticked
    ? t('tick.ariaLabelTicked', { name: item.ingredientName })
    : t('tick.ariaLabel', { name: item.ingredientName, quantity: item.quantity, unit: item.canonicalUnit })

  function handleTick() {
    haptic('light')
    onTick(item)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === ' ') {
      e.preventDefault()
      handleTick()
    }
  }

  return (
    <div
      role="checkbox"
      aria-checked={item.ticked}
      aria-label={ariaLabel}
      tabIndex={0}
      onClick={handleTick}
      onKeyDown={handleKeyDown}
      className={[
        'flex items-center gap-3 min-h-[56px] px-4 py-2 cursor-pointer',
        'transition-opacity',
        item.ticked ? 'opacity-60' : 'opacity-100',
        !reducedMotion ? 'duration-200' : '',
      ].join(' ')}
    >
      {/* Tick circle — 40×40px touch target */}
      <div className="relative flex-shrink-0 h-10 w-10 flex items-center justify-center">
        <div
          className={[
            'h-6 w-6 rounded-full border-2 flex items-center justify-center',
            item.ticked
              ? 'bg-accent border-accent'
              : 'border-muted bg-transparent',
            !reducedMotion && item.ticked ? 'animate-[checkFill_200ms_ease-in-out]' : '',
          ].join(' ')}
          aria-hidden="true"
        >
          {item.ticked && (
            <svg
              className="h-3 w-3 text-canvas"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M2 6l3 3 5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="24"
                strokeDashoffset={!reducedMotion ? undefined : '0'}
              />
            </svg>
          )}
        </div>
      </div>

      {/* Item text */}
      <div className="flex-1">
        <p
          className={[
            'font-medium',
            item.ticked ? 'line-through text-muted' : 'text-ink',
          ].join(' ')}
        >
          {item.ingredientName}
        </p>
        <p className="text-sm text-muted">
          {item.quantity} {item.canonicalUnit}
        </p>
      </div>

      {isPendingSync && (
        <span
          aria-label={t('tick.pendingSyncLabel')}
          className="ml-2 text-xs text-muted-foreground"
        >
          ↻
        </span>
      )}
    </div>
  )
}
